/**
 * `single_choice` renderer — composes `@nextday/funnel-ui` `SingleChoice`.
 *
 * Stores the chosen option id under `data.answerKey`. When `data.autoAdvance`
 * is true (default), the renderer fires `onAdvance()` after the funnel-ui
 * component's internal selection feedback delay (220ms) — the renderer just
 * mirrors that hand-off; the underlying component owns the timer.
 *
 * Step JSON shape (plan §5 step 1):
 *
 *   {
 *     "type": "single_choice",
 *     "data": {
 *       "question": "What kind of connection are you looking for today?",
 *       "description": "...",
 *       "layout": "row",
 *       "autoAdvance": true,
 *       "answerKey": "connection_intent",
 *       "options": [
 *         { "id": "listener", "label": "A good listener", "description": "..." },
 *         ...
 *       ]
 *     }
 *   }
 */
import { SingleChoice } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import { choiceOptionSchema } from "@survey-kit/renderers/choiceOptionSchema";
import type { RendererProps } from "@survey-kit/types";

export const singleChoiceSchema = z
  .object({
    question: z.string().min(1),
    description: z.string().optional(),
    eyebrow: z.string().optional(),
    options: z.array(choiceOptionSchema).min(2),
    /** State key under which the chosen option id is recorded. */
    answerKey: z.string().min(1),
    layout: z.enum(["row", "tile", "cover"]).optional(),
    columns: z.union([z.literal(1), z.literal(2)]).optional(),
    /** When false, hides the radio indicator on row layout. Defaults to true. */
    showIndicator: z.boolean().optional(),
    autoAdvance: z.boolean().optional(),
  })
  .strict();

export type SingleChoiceData = z.infer<typeof singleChoiceSchema>;

const SingleChoiceRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onAdvance,
}: RendererProps<SingleChoiceData>) => {
  const initial =
    typeof answers[data.answerKey] === "string" ? (answers[data.answerKey] as string) : undefined;
  const autoAdvance = data.autoAdvance ?? true;

  const handleAnswer = (optionId: string): void => {
    onAnswer(data.answerKey, { value: optionId });
    if (autoAdvance) onAdvance();
  };

  return (
    <SingleChoice
      question={data.question}
      description={data.description}
      eyebrow={data.eyebrow}
      options={data.options}
      initialValue={initial}
      layout={data.layout}
      columns={data.columns}
      showIndicator={data.showIndicator}
      autoAdvance={autoAdvance}
      onAnswer={handleAnswer}
      testId={`renderer-single-choice-${step.id}`}
    />
  );
};

export const singleChoiceRenderer = createRenderer({
  type: "single_choice",
  dataSchema: singleChoiceSchema,
  label: "Single choice",
  Component: SingleChoiceRenderer,
});

export default singleChoiceRenderer;
